import { coalesce } from "./coalesce";

/**
 * Given a function that takes a resolve and reject function and returns a
 * promise (like a resolver function), and lets you treat it like a normal
 * promise callback... only async! (Async Promise callbacks should avoid
 * themselves returning promises.)
 *
 * ⚠️ This works just like `new Promise`, so don't forget to call `resolve`
 * or `reject`!
 *
 * @param resolver - A function that takes a resolve and reject function and returns a promise.
 * @returns A promise that resolves with the value of the resolver function.
 * @example
 * ```ts
 * const result = await executeAsyncPromiseCallback(async (resolve, reject) => {
 *   // do something async
 *   resolve(await doThing());
 * }))
 * ```
 */
export function executeAsyncPromiseCallback<T = void>(
  resolver: (
    resolve: (value: T) => void,
    reject?: (reason?: any) => void
  ) => Promise<void>
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let hasResolved = false;
    let resolveValue: T;
    let rejectReason: any;
    const fauxResolve = (value: T) => {
      if (hasResolved) {
        return;
      }
      resolveValue = value;
      hasResolved = true;
    };
    const fauxReject = (reason?: any) => {
      if (hasResolved) {
        return;
      }
      rejectReason = reason;
      hasResolved = true;
    };
    try {
      resolver(fauxResolve, fauxReject)
        .then(() => {
          if (rejectReason !== undefined) {
            reject(rejectReason);
          } else {
            resolve(resolveValue);
          }
        })
        .catch((error) => reject(coalesce(rejectReason, error)));
    } catch (error) {
      reject(error);
    }
  });
}
